import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Box, Card, Chip, Stack, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation, untranslated } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { useSelector } from "react-redux";
import { checklistsInReviews } from "./ReviewsHelper";
import CommentModal from "./CommentModal";
import IconComponent from "@CubeComponents/icons";
import useDebounce from "@Components/Search/useDebounce";
import { RootState } from "@Reducers/index";
import type { ReviewDataResult } from "@ServerTypes/reviews/reviews.schema";

const CHECKLIST_HEADER_HEIGHT = "2.75rem";
const SECTION_HEADER_HEIGHT = "2.5rem";
const HORIZONTAL_PADDING = 3;

interface ChecklistModalProps {
  fileVersionId: any;
  closeModal: () => void;
  reviewId: any;
  refreshData: any;
  projectId: any;
}

export default function ChecklistModal({
  fileVersionId,
  closeModal,
  reviewId,
  refreshData,
  projectId,
}: ChecklistModalProps) {
  const { t } = useTranslation();
  const reviewData = useSelector((state: RootState) => state.reviewData) as ReviewDataResult & {
    approvalStages: NonNullable<ReviewDataResult["approvalStages"]>;
    reviewStatus: NonNullable<ReviewDataResult["reviewStatus"]>;
    reviewFlow: NonNullable<ReviewDataResult["reviewFlow"]>;
  };

  const [showCurrentLevelOnly, setShowCurrentLevelOnly] = useState<boolean>(true);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [commentModalOpen, setCommentModalOpen] = useState<boolean>(false);
  const [commentTarget, setCommentTarget] = useState<any>(null);

  const pendingSaveRef = useRef<any>(null);

  const currentResource = reviewData.resources?.find((resource: any) => resource.fileVersionId === fileVersionId);
  const currentLevelId = reviewData.currentReviewLevel?.levelId;

  const getQuestionKey = useCallback((checklistId: string, levelId: string, questionId: string): string => {
    return `${checklistId}_${levelId}_${questionId}`;
  }, []);

  const allChecklists = useMemo(() => {
    if (!currentResource) return [];

    const results: any[] = [];
    const assignments = currentResource.checklistAssignments || [];

    assignments.forEach((assignment: any) => {
      const checklist = reviewData.reviewFlow.checklists?.find((c: any) => c._id === assignment.checklistId);
      if (!checklist) return;

      assignment.levelIds?.forEach((levelId: any) => {
        const level = reviewData.reviewFlow.reviewLevels?.find((l: any) => l._id === levelId);
        if (!level) return;

        results.push({
          ...checklist,
          levelId,
          levelNumber: Number(level.serial) || 0,
          isCurrent: levelId === currentLevelId,
        });
      });
    });

    results.sort((a, b) => {
      if (a.levelNumber !== b.levelNumber) return a.levelNumber - b.levelNumber;
      return String(a.title).localeCompare(String(b.title));
    });

    return results;
  }, [currentResource, reviewData.reviewFlow, currentLevelId]);

  const visibleChecklists = useMemo(() => {
    return showCurrentLevelOnly ? allChecklists.filter((c: any) => c.isCurrent) : allChecklists;
  }, [allChecklists, showCurrentLevelOnly]);

  useEffect(() => {
    if (!currentResource) {
      setAnswers({});
      return;
    }

    const selections = currentResource.checklistSelections || {};
    const loaded: Record<string, string[]> = {};

    allChecklists.forEach((checklist: any) => {
      checklist.template?.forEach((section: any) => {
        section.questionFields?.forEach((question: any) => {
          const questionId = question._id;
          const key = getQuestionKey(checklist._id, checklist.levelId, questionId);
          const saved = selections[checklist.levelId]?.[checklist._id]?.[questionId];
          loaded[key] = (saved?.selected || []).map(String);
        });
      });
    });

    setAnswers(loaded);
  }, [currentResource, fileVersionId, allChecklists, getQuestionKey]);

  const saveImmediately = useCallback(
    async (checklist: any, questionId: string, selected: string[]) => {
      await checklistsInReviews({
        reviewId,
        fileVersionId,
        levelId: checklist.levelId,
        checklistId: checklist._id,
        questionId,
        selected,
        projectId,
      });
    },
    [reviewId, fileVersionId, projectId],
  );

  const debouncedSaveAnswer = useDebounce(async (checklist: any, questionId: string, selected: string[]) => {
    await saveImmediately(checklist, questionId, selected);
    pendingSaveRef.current = null;
  }, 200);

  const saveAnswer = useCallback(
    (checklist: any, questionId: string, selected: string[]) => {
      pendingSaveRef.current = { checklist, questionId, selected };
      debouncedSaveAnswer(checklist, questionId, selected);
    },
    [debouncedSaveAnswer],
  );

  const toggleChoice = useCallback(
    (checklist: any, questionId: string, choiceIndex: number) => {
      const key = getQuestionKey(checklist._id, checklist.levelId, questionId);
      const choices = checklist.choices || [];
      const choiceId = choices[choiceIndex]?._id?.toString();

      if (!choiceId) {
        console.error("Choice ID not found for index", choiceIndex);
        return;
      }

      setAnswers((prev) => {
        const current = new Set(prev[key] || []);
        const allowMultiple = checklist.allowMultiple !== false;

        if (allowMultiple) {
          current.has(choiceId) ? current.delete(choiceId) : current.add(choiceId);
        } else {
          if (current.size === 1 && current.has(choiceId)) {
            current.clear();
          } else {
            current.clear();
            current.add(choiceId);
          }
        }

        const selected = Array.from(current);
        saveAnswer(checklist, questionId, selected);
        return { ...prev, [key]: selected };
      });
    },
    [saveAnswer, getQuestionKey],
  );

  const getChecklistStatus = useCallback(
    (checklist: any): "filled" | "in-progress" | "skipped" | "future" => {
      const sections = checklist.template || [];
      const totalQuestions = sections.reduce((sum: number, s: any) => sum + (s.questionFields?.length || 0), 0);
      if (totalQuestions === 0) return "skipped";

      let answeredCount = 0;
      sections.forEach((section: any) => {
        (section.questionFields || []).forEach((question: any) => {
          const key = getQuestionKey(checklist._id, checklist.levelId, question._id);
          const answer = answers[key];
          if (answer && answer.length > 0) {
            answeredCount++;
          }
        });
      });
      if (checklist.isCurrent) {
        return "in-progress";
      }

      const currentLevel = reviewData.reviewFlow.reviewLevels?.find((l: any) => l._id === currentLevelId);
      const currentLevelNumber = Number(currentLevel?.serial) || 0;
      if (!currentLevelId || checklist.levelNumber <= currentLevelNumber) {
        return answeredCount > 0 ? "filled" : "skipped";
      }
      return "future";
    },
    [answers, getQuestionKey, reviewData.reviewFlow.reviewLevels, currentLevelId],
  );

  const getCommentCount = useCallback(
    (checklist: any, questionId: string): number => {
      return (
        currentResource?.checklistSelections?.[checklist.levelId]?.[checklist._id]?.[questionId]?.commentCount || 0
      );
    },
    [currentResource],
  );

  const openComments = useCallback(
    async (checklist: any, questionId: string, isReadOnly: boolean) => {
      if (pendingSaveRef.current) {
        const { checklist: c, questionId: qId, selected } = pendingSaveRef.current;
        await saveImmediately(c, qId, selected);
        pendingSaveRef.current = null;
      }

      setCommentTarget({
        checklistId: checklist._id,
        levelId: checklist.levelId,
        questionId,
        readOnly: isReadOnly,
        choices: checklist.choices || [],
      });
      setCommentModalOpen(true);
    },
    [saveImmediately],
  );

  const closeComments = useCallback(() => {
    setCommentModalOpen(false);
    setCommentTarget(null);
    if (refreshData) {
      refreshData(true, true, true);
    }
  }, [refreshData]);

  return (
    <>
      {commentModalOpen && commentTarget && (
        <CommentModal
          open={commentModalOpen}
          onClose={closeComments}
          reviewId={reviewId}
          fileVersionId={fileVersionId}
          levelId={commentTarget.levelId}
          checklistId={commentTarget.checklistId}
          questionId={commentTarget.questionId}
          checkboxChoices={commentTarget.choices}
          readOnly={commentTarget.readOnly}
          projectId={projectId}
        />
      )}

      <ModalStateDialogComponent
        isVisible
        allowModalPadding={false}
        width="100%"
        headerTitle={t("Checklists")}
        modelContent={
          <Box sx={{ maxHeight: "40.625rem", overflowY: "auto", px: HORIZONTAL_PADDING, pb: "1rem" }}>
            <Box sx={{ display: "flex", alignItems: "center", py: "1rem", gap: "1rem" }}>
              <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                {t("Show only checklists to be filled at this level")}
              </Typography>
              <ToggleSwitchComponent
                checked={showCurrentLevelOnly}
                onChange={(e) => setShowCurrentLevelOnly(e.target.checked)}
              />
            </Box>
            {visibleChecklists.map((checklist: any) => {
              const choices = checklist.choices || [];
              const canEdit = checklist.isCurrent;
              const status = getChecklistStatus(checklist);

              const statusConfig = {
                filled: {
                  label: t("Filled"),
                  color: "#4caf50",
                  backgroundColor: "#e8f5e9",
                },
                "in-progress": {
                  label: t("In Progress"),
                  color: "#ff9800",
                  backgroundColor: "#fff3e0",
                },
                skipped: {
                  label: t("Skipped"),
                  color: "#9e9e9e",
                  backgroundColor: "#f5f5f5",
                },
                future: {
                  label: t("Future"),
                  color: "#2196f3",
                  backgroundColor: "#e3f2fd",
                },
              };

              const currentStatus = statusConfig[status];

              return (
                <Card
                  key={`${checklist._id}-${checklist.levelId}`}
                  sx={{
                    mb: "1rem",
                    borderRadius: "0.5rem",
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "none",
                  }}
                >
                  <Accordion disableGutters defaultExpanded sx={{ boxShadow: "none" }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        minHeight: CHECKLIST_HEADER_HEIGHT,
                        "& .MuiAccordionSummary-content": { m: 0, alignItems: "center" },
                        px: 2,
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="subtitle1" fontWeight={700}>
                            {checklist.title}
                          </Typography>

                          <Chip
                            label={currentStatus.label}
                            size="small"
                            icon={
                              <Box
                                sx={{
                                  width: "0.5rem",
                                  height: "0.5rem",
                                  borderRadius: "50%",
                                  backgroundColor: currentStatus.color,
                                  ms: 1,
                                }}
                              />
                            }
                            sx={{
                              backgroundColor: currentStatus.backgroundColor,
                              color: "#000000",
                              fontWeight: 500,
                              border: "none",
                              "& .MuiChip-icon": {
                                ms: 1,
                                me: -0.5,
                              },
                            }}
                          />
                        </Stack>

                        <Chip
                          label={`${t("LEVEL")} ${checklist.levelNumber}`}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "1rem",
                            backgroundColor: "white",
                            height: "1.5rem",
                          }}
                        />
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0 }}>
                      {(checklist.template || []).map((section: any, sectionIndex: number) => {
                        const questions = section.questionFields || [];

                        return (
                          <Accordion
                            key={sectionIndex}
                            disableGutters
                            defaultExpanded
                            sx={{
                              boxShadow: "none",
                              "&:before": { display: "none" },
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                minHeight: SECTION_HEADER_HEIGHT,
                                bgcolor: (theme: Theme) =>
                                  theme.palette.mode === "light" ? "#f6f7f9" : "rgba(255,255,255,0.03)",
                                px: 2,
                                "& .MuiAccordionSummary-content": { m: 0, alignItems: "center" },
                              }}
                            >
                              <Stack direction="row" alignItems="center" sx={{ width: "100%" }}>
                                <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                                  {section.sectionHeading || t("Section")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {questions.length} {t("items")}
                                </Typography>
                              </Stack>
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: 0 }}>
                              <Box>
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: `minmax(12.5rem, 1fr) repeat(${choices.length}, 5rem) 4.375rem`,
                                    alignItems: "center",
                                    px: 2,
                                    py: "0.5rem",
                                    bgcolor: (theme: Theme) =>
                                      theme.palette.mode === "light" ? "#f6f7f9" : "rgba(255,255,255,0.04)",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      color: (theme: Theme) => theme.palette.text.primary,
                                    }}
                                  >
                                    {t("Checkpoints")}
                                  </Typography>

                                  {choices.map((choice: any, choiceIndex: number) => (
                                    <Typography
                                      key={choiceIndex}
                                      variant="subtitle2"
                                      align="center"
                                      sx={{
                                        fontWeight: 600,
                                        color: (theme: Theme) => theme.palette.text.primary,
                                        px: 0.5,
                                        wordWrap: "break-word",
                                        overflowWrap: "break-word",
                                      }}
                                    >
                                      {untranslated(choice.value)}
                                    </Typography>
                                  ))}

                                  <Typography
                                    variant="subtitle2"
                                    align="center"
                                    sx={{
                                      fontWeight: 600,
                                      color: (theme: Theme) => theme.palette.text.primary,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {t("Comments")}
                                  </Typography>
                                </Box>
                                {questions.map((question: any, questionIndex: number) => {
                                  const questionId = question._id;
                                  const key = getQuestionKey(checklist._id, checklist.levelId, questionId);
                                  const selected = answers[key] || [];
                                  const commentCount = getCommentCount(checklist, questionId);

                                  return (
                                    <Box
                                      key={questionId}
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns: `minmax(12.5rem, 1fr) repeat(${choices.length}, 5rem) 4.375rem`,
                                        alignItems: "center",
                                        px: 2,
                                        py: "0.5rem",
                                        borderTop: "1px solid",
                                        borderColor: "divider",
                                        bgcolor: questionIndex % 2 === 0 ? "#fff" : "#f9fafb",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "contents",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            gridColumn: `1 / span ${1 + choices.length}`,
                                            display: "grid",
                                            gridTemplateColumns: `minmax(12.5rem, 1fr) repeat(${choices.length}, 5rem)`,
                                            opacity: canEdit ? 1 : 0.5,
                                          }}
                                        >
                                          <Typography variant="body2" sx={{ color: "text.primary", pe: 2 }}>
                                            {question.label}
                                          </Typography>
                                          {choices.map((choice: any, choiceIndex: number) => {
                                            const choiceId = choice._id?.toString();
                                            return (
                                              <Box
                                                key={choiceIndex}
                                                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                                              >
                                                <CheckBoxComponent
                                                  checked={selected.includes(choiceId)}
                                                  onChange={() =>
                                                    canEdit && toggleChoice(checklist, questionId, choiceIndex)
                                                  }
                                                  disabled={!canEdit}
                                                />
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      </Box>
                                      <Box
                                        onClick={() => openComments(checklist, questionId, !canEdit)}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: "0.25rem",
                                          minWidth: "2.125rem",
                                          height: "1.5rem",
                                          px: 1,
                                          border: "1px solid",
                                          borderColor: "divider",
                                          borderRadius: "0.375rem",
                                          cursor: "pointer",
                                          opacity: 1,
                                          "&:hover": {
                                            backgroundColor: (theme: Theme) => theme.palette.action.hover,
                                          },
                                        }}
                                      >
                                        <IconComponent name="comment" width="1.25rem" height="1.25rem" />
                                        {commentCount > 0 && (
                                          <Typography
                                            variant="caption"
                                            sx={{ fontSize: "0.75rem", fontWeight: 500, color: "text.primary" }}
                                          >
                                            {commentCount}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                </Card>
              );
            })}
          </Box>
        }
        onCancel={closeModal}
      />
    </>
  );
}
